import { TRole } from '../model/IRole';
import { pagesAccess, TPageUrl } from './accessMap';

// --
// -- Which page a URL belongs to, and which pages a person can open.
// --
// -- Kept out of the hook and the route table so both answer the same way, and
// -- so the rules can be tested without a browser or a signed-in session.
// --

/** Every real page, in nav order. Sign in is a route, not a destination. */
export const PAGES = (Object.keys(pagesAccess) as TPageUrl[])
  .filter((page) => page !== '/oauth/signIn');

/**
 * The page a concrete URL belongs to, or null if it is not one of ours.
 *
 * Matches the whole path or a path below it, never a prefix of a sibling:
 * "/orders-by-date" starts with "/orders", so a plain startsWith would hand
 * every report the Recent Orders permission.
 */
export const pageKeyOf = (path: string): TPageUrl | null => {
  const clean = (path.split('?')[0].split('#')[0]) || '/';
  const keys = Object.keys(pagesAccess) as TPageUrl[];

  const exact = keys.find((key) => clean === key);
  if (exact) return exact;

  // Longest first, so a nested route lands on the most specific page.
  return keys
    .filter((key) => clean.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0] ?? null;
};

/** Does any of these roles open this page? */
export const rolesAllow = (roles: TRole[], page: TPageUrl): boolean => {
  const allowed = pagesAccess[page] || [];
  return roles.some((role) => allowed.includes(role));
};

/**
 * Where to send someone who has just signed in, or null if there is nowhere.
 *
 * Null is not a redirect - it is the case where the app has to say so. Sending
 * them anywhere would land on a page that bounces them back to sign in, which
 * bounces them here again: the loop.
 */
export const firstAccessiblePage = (can: (page: TPageUrl) => boolean): TPageUrl | null =>
  PAGES.find(can) ?? null;
